import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import {
  LayoutDashboard, Users, CalendarDays, Stethoscope, Receipt, Settings, LogOut,
  Sparkles, BarChart3, UserCircle2, ShieldCheck, Package, ClipboardList, Bell,
  MessageCircle, Book, CalendarClock, Menu, X,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import InstallPWAButton from "@/components/InstallPWAButton";

const NAV = [
  { to: "/", label: "Paneli Kryesor", icon: LayoutDashboard, end: true, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/pacientet", label: "Pacientët", icon: Users, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/takimet", label: "Takimet", icon: CalendarDays, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/lajmerime", label: "Lajmërime", icon: MessageCircle, roles: ["admin", "doctor", "receptionist"] },
  { to: "/kujtuesit", label: "Kujtuesit", icon: Bell, roles: ["admin", "doctor", "receptionist"] },
  { to: "/trajtimet", label: "Trajtimet", icon: Stethoscope, roles: ["admin", "doctor", "assistant"] },
  { to: "/procedurat", label: "Procedurat", icon: ClipboardList, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/inventari", label: "Inventari", icon: Package, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/faturimi", label: "Faturimi", icon: Receipt, roles: ["admin", "doctor", "receptionist"] },
  { to: "/raportet", label: "Raportet", icon: BarChart3, roles: ["admin", "doctor", "receptionist"] },
  { to: "/profili", label: "Profili", icon: UserCircle2, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/udhezues", label: "Udhëzuesi", icon: Book, roles: ["admin", "doctor", "receptionist", "assistant"] },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, roles: ["admin"] },
  { to: "/orari-doktoreve", label: "Orari i Doktorëve", icon: CalendarClock, roles: ["admin"] },
  { to: "/cilesimet", label: "Cilësimet", icon: Settings, roles: ["admin"] },
];

const ROLE_LABEL = {
  admin: "Administrator", doctor: "Mjek Stomatolog",
  receptionist: "Recepsionist", assistant: "Asistent",
};

function SidebarBody({ mobile = false, clinicName, items, user, onLogout, onClose }) {
  return (
    <>
      <div className="flex items-center gap-3 px-6 py-6 border-b border-[#E2DFD8]">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2C4C3B] text-[#FDFBF7]">
          <Sparkles size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold tracking-tight truncate"
               data-testid={mobile ? "sidebar-clinic-name-mobile" : "sidebar-clinic-name"}>
            {clinicName}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C]">Sistemi Dentar</div>
        </div>
        {mobile && (
          <button onClick={onClose}
                  className="p-1.5 rounded-md text-[#5C5C5C] hover:text-[#1A1A1A]"
                  aria-label="Mbyll menunë"
                  data-testid="drawer-close-btn">
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"
           data-testid={mobile ? "sidebar-nav-mobile" : "sidebar-nav"}>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
            className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#E2DFD8] p-4">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAE4D9] text-[#2C4C3B] font-semibold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-[#1A1A1A]" data-testid="user-name">{user?.name}</div>
            <div className="truncate text-xs text-[#5C5C5C]">{ROLE_LABEL[user?.role]}</div>
          </div>
        </div>
        <button onClick={onLogout}
                className="mt-2 nav-link w-full"
                data-testid={mobile ? "logout-button-drawer" : "logout-button"}>
          <LogOut size={18} /> Dil
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const clinicName = settings?.clinic_name || "DentArt";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="flex min-h-screen bg-[#FDFBF7]" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#E2DFD8] bg-white">
        <SidebarBody clinicName={clinicName} items={items} user={user} onLogout={onLogout} />
      </aside>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} aria-hidden />
          <aside className="absolute left-0 top-0 h-full w-72 max-w-[85vw] flex flex-col bg-white shadow-xl">
            <SidebarBody mobile clinicName={clinicName} items={items} user={user}
                         onLogout={onLogout} onClose={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-[#E2DFD8] bg-white/95 backdrop-blur px-4 py-3"
                style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <button onClick={() => setDrawerOpen(true)}
                  className="p-2 rounded-md text-[#1A1A1A] hover:bg-[#EAE4D9]"
                  aria-label="Hap menunë"
                  data-testid="drawer-open-btn">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2C4C3B] text-[#FDFBF7]">
              <Sparkles size={14} />
            </div>
            <span className="font-semibold tracking-tight truncate">{clinicName}</span>
          </div>
          <button onClick={onLogout}
                  className="p-2 rounded-md text-[#5C5C5C] hover:text-[#1A1A1A] hover:bg-[#EAE4D9]"
                  aria-label="Dil"
                  data-testid="logout-button-mobile">
            <LogOut size={18} />
          </button>
        </header>

        <div className="flex-1 px-4 py-6 md:px-10 md:py-10"
             style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
          <Outlet />
        </div>
      </main>

      <InstallPWAButton />
      <Toaster richColors position="top-right" />
    </div>
  );
}
